import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { checkMessage } from '@/lib/zodMessages';
import { useLingui } from '@lingui/react';

import { BaseEditableForm } from '@/components/form/BaseEditableForm';
import type { FieldConfig, GroupConfig, EnhancedSelectOption } from '@/components/form/types';
import { ChildrenService } from '@/api/services/ChildrenService';
import { ContactsService } from '@/api/services/ContactsService';
import { useCreateChangeRequest } from '@/hooks/useChildDataUpdateRequest';
import {
  CHANGE_REQUEST_SUB_TYPES,
  COMMENT_FIELDS,
  buildCreateChangeRequestBatch,
  getCommentFieldLabel,
  getSubTypeLabel,
  isSensitiveSubType,
  type ChangeRequestFormValues,
  type ChangeRequestSourceKind,
} from '@/types/changeRequest';
import { Badge } from '@/components/ui/badge';
import type { change_requests_ChangeRequestResultDTO } from '@/api';

interface ChildDataUpdateRequestFormProps {
  /** Prefill the target children (e.g. single-child launch from a child page). */
  initialChildIds?: string[];
  /**
   * Display labels for the prefilled/locked targets, so the picker shows the
   * child's name (not the raw id) without a list fetch. Entry-point pages that
   * already have the child loaded should pass this; missing ids fall back to id.
   */
  initialChildOptions?: EnhancedSelectOption[];
  /** Full prefill: sub_type + change fields + targets (e.g. an email-sourced item in review mode). */
  prefill?: Partial<ChangeRequestFormValues> & { subType?: string };
  /** 'create' (default) opens blank/prefilled; 'review' re-submits an edited extracted request. */
  mode?: 'create' | 'review';
  /** Disable the child picker (entry-point single-child launch). */
  lockTarget?: boolean;
  /** Producer source — only the kind differs across channels; defaults to web_admin. */
  source?: { kind: ChangeRequestSourceKind; message_id?: string };
  /** Receives the per-request result (routing class, created item) after a successful create. */
  onResult?: (result: change_requests_ChangeRequestResultDTO | undefined) => void;
  /**
   * Review-mode in-place submit. When provided, submit calls this (with the
   * submitted values + a `wasEdited` flag computed by deep-comparing the values to
   * the normalized prefill baseline) INSTEAD of creating a new request — the triage
   * drawer uses it to PATCH the item payload then approve. Backward-compatible: when
   * absent, the #737 create + "Edit & re-submit" paths are unchanged.
   */
  onReviewSubmit?: (values: ChangeRequestFormValues, wasEdited: boolean) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Field-by-field equality of two form-value snapshots (childIds compared order-
// insensitively). Used to compute `wasEdited` for the in-place review submit so the
// server records `edited` only when the reviewer actually changed something.
function isSameFormValues(
  a: Partial<ChangeRequestFormValues>,
  b: Partial<ChangeRequestFormValues>,
): boolean {
  // NOTE: keep this list in sync with the scalar fields of `ChangeRequestFormValues`
  // (changeRequest.ts) — plus `childIds`, compared separately below. A new form
  // field added there but missing here would be silently ignored by the diff, so a
  // real edit could be misclassified as `approved_as_is` instead of `edited`.
  const keys: (keyof ChangeRequestFormValues)[] = [
    'subType',
    'commentField',
    'commentValue',
    'allergyOperation',
    'allergyId',
    'allergyName',
    'allergySeverity',
    'allergyComment',
    'medicalKind',
    'medicalOperation',
    'medicalId',
    'medicalName',
    'medicalType',
    'medicalDosage',
    'medicalComment',
    'contactId',
    'contactGrant',
  ];
  const aChildren = [...((a.childIds as string[]) ?? [])].sort();
  const bChildren = [...((b.childIds as string[]) ?? [])].sort();
  if (JSON.stringify(aChildren) !== JSON.stringify(bChildren)) return false;
  return keys.every((k) => a[k] === b[k]);
}

// Bulk cap — the generic executor.body.child_ids is not server-validated for
// count, so the client bounds per-child apply work (mirrors the standalone DTO).
const MAX_BULK_CHILDREN = 200;

// Canonical flat form value shape; mapped to the nested ChildDataUpdateSpec on submit.
type FormValues = ChangeRequestFormValues;

export function ChildDataUpdateRequestForm({
  initialChildIds,
  initialChildOptions,
  prefill,
  mode = 'create',
  lockTarget = false,
  source,
  onResult,
  onReviewSubmit,
  onSuccess,
  onCancel,
}: ChildDataUpdateRequestFormProps) {
  const { i18n } = useLingui();
  const initialSubType = prefill?.subType ?? 'comment';
  const [subType, setSubType] = useState<string>(initialSubType);
  // Tracks the parent-driven in-place review submit (PATCH + approve) so the form's
  // submit button shows a spinner while it runs.
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  // Wrapper ref for first-field autofocus + Cmd/Ctrl+Enter submit.
  const containerRef = useRef<HTMLDivElement>(null);

  const create = useCreateChangeRequest();

  const targetChildIds = useMemo(
    () => prefill?.childIds ?? initialChildIds ?? [],
    [prefill?.childIds, initialChildIds],
  );

  // Stable identity of the request this form is editing. `subType` (below) and the
  // BaseEditableForm's react-hook-form values are seeded only at mount, so when a
  // parent reuses this same mounted form for a different request (e.g. swapping the
  // reviewed pending item without unmounting), this key changes to remount the form
  // and reset `subType` — preventing the previous request's extracted values from
  // leaking into the next submit.
  const prefillKey = useMemo(
    () => JSON.stringify({ mode, initialSubType, prefill: prefill ?? null, childIds: targetChildIds }),
    [mode, initialSubType, prefill, targetChildIds],
  );

  // Reset the local subType when the edited request identity changes.
  useEffect(() => {
    setSubType(prefill?.subType ?? 'comment');
    // prefillKey folds in prefill + targets + mode; resetting only on identity change
    // keeps in-form subType edits intact.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  // Server-side search for the target/contact pickers, so the form mounts without
  // eagerly loading the full children/contacts lists. Contacts are queried only
  // when a contact sub_type is selected (that field only mounts then).
  const loadChildOptions = useCallback(async (query: string): Promise<EnhancedSelectOption[]> => {
    const res = await ChildrenService.getApiV1Children(50, 0, undefined, query.trim() || undefined);
    return (res.children ?? [])
      .filter((c) => !!c.id)
      .map((c) => ({
        value: c.id as string,
        label: `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || (c.id as string),
      }));
  }, []);

  const loadContactOptions = useCallback(async (query: string): Promise<EnhancedSelectOption[]> => {
    const res = await ContactsService.getApiV1Contacts(50, 0, undefined, query.trim() || undefined);
    return (res.contacts ?? [])
      .filter((c) => !!c.id)
      .map((c) => ({
        value: c.id as string,
        label: `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || (c.id as string),
      }));
  }, []);

  // Seed the pickers so prefilled/locked targets render with a label without
  // forcing a list fetch. Prefer caller-supplied labels (e.g. the child's name
  // from the entry-point page); fall back to the raw id when none is provided.
  const childInitialOptions = useMemo<EnhancedSelectOption[]>(() => {
    const labelById = new Map(
      (initialChildOptions ?? []).map((opt) => [String(opt.value), opt.label]),
    );
    return targetChildIds.map((id) => ({ value: id, label: labelById.get(id) ?? id }));
  }, [initialChildOptions, targetChildIds]);

  const schema = useMemo(
    () =>
      z
        .object({
          childIds: z.array(z.string()).min(1, checkMessage.selectAtLeast(i18n)).max(MAX_BULK_CHILDREN, {
            message: i18n._({
              id: 'changeRequest.validation.maxChildren',
              message: 'Select at most {max} children per request',
              values: { max: MAX_BULK_CHILDREN },
            }),
          }),
          subType: z.string().min(1, checkMessage.required(i18n)),
          commentField: z.string().optional().default('notes'),
          commentValue: z.string().optional().default(''),
          allergyOperation: z.string().optional().default('add'),
          allergyId: z.string().optional().default(''),
          allergyName: z.string().optional().default(''),
          allergySeverity: z.string().optional().default(''),
          allergyComment: z.string().optional().default(''),
          medicalKind: z.string().optional().default('disease'),
          medicalOperation: z.string().optional().default('add'),
          medicalId: z.string().optional().default(''),
          medicalName: z.string().optional().default(''),
          medicalType: z.string().optional().default(''),
          medicalDosage: z.string().optional().default(''),
          medicalComment: z.string().optional().default(''),
          contactId: z.string().optional().default(''),
          contactGrant: z.boolean().optional().default(true),
        })
        .superRefine((val, ctx) => {
          const required = i18n._({ id: 'changeRequest.validation.required', message: 'Required' });
          if (val.subType === 'comment' && !val.commentValue?.trim()) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['commentValue'], message: required });
          }
          if (val.subType === 'allergy') {
            if (val.allergyOperation === 'add' && !val.allergyName?.trim()) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allergyName'], message: required });
            }
            if ((val.allergyOperation === 'update' || val.allergyOperation === 'remove') && !val.allergyId) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allergyId'], message: required });
            }
          }
          if (val.subType === 'medical') {
            if (val.medicalOperation === 'add' && !val.medicalName?.trim()) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['medicalName'], message: required });
            }
            if ((val.medicalOperation === 'update' || val.medicalOperation === 'remove') && !val.medicalId) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['medicalId'], message: required });
            }
          }
          if ((val.subType === 'contact_pickup' || val.subType === 'contact_emergency') && !val.contactId) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactId'], message: required });
          }
        }),
    [i18n],
  ) as unknown as z.ZodType<FormValues, FormValues>;

  const fields: FieldConfig<FormValues>[] = useMemo(() => {
    const operationOptions: EnhancedSelectOption[] = [
      { value: 'add', label: i18n._({ id: 'changeRequest.op.add', message: 'Add' }) },
      { value: 'update', label: i18n._({ id: 'changeRequest.op.update', message: 'Update' }) },
      { value: 'remove', label: i18n._({ id: 'changeRequest.op.remove', message: 'Remove' }) },
    ];

    const base: FieldConfig<FormValues>[] = [
      {
        name: 'childIds',
        label: i18n._({ id: 'changeRequest.field.children', message: 'Children' }),
        type: 'multiselect',
        required: true,
        disabled: lockTarget,
        colSpan: 2,
        // Locked launch: render the (disabled) picker from the locked ids without
        // a fetch. Otherwise search children server-side via loadOptions.
        ...(lockTarget
          ? { selectOptions: childInitialOptions }
          : { loadOptions: loadChildOptions, initialOptions: childInitialOptions }),
      },
      {
        name: 'subType',
        label: i18n._({ id: 'changeRequest.field.subType', message: 'Change type' }),
        type: 'select',
        required: true,
        selectOptions: CHANGE_REQUEST_SUB_TYPES.map((st) => ({
          value: st,
          label: getSubTypeLabel(i18n, st),
        })),
      },
    ];

    if (subType === 'comment') {
      base.push(
        {
          name: 'commentField',
          label: i18n._({ id: 'changeRequest.field.commentField', message: 'Field' }),
          type: 'select',
          selectOptions: COMMENT_FIELDS.map((f) => ({ value: f, label: getCommentFieldLabel(i18n, f) })),
        },
        {
          name: 'commentValue',
          label: i18n._({ id: 'changeRequest.field.commentValue', message: 'Value' }),
          type: 'textarea',
          colSpan: 2,
        },
      );
    } else if (subType === 'allergy') {
      base.push(
        { name: 'allergyOperation', label: i18n._({ id: 'changeRequest.field.operation', message: 'Operation' }), type: 'select', selectOptions: operationOptions },
        { name: 'allergyId', label: i18n._({ id: 'changeRequest.field.allergyId', message: 'Allergy ID' }), type: 'text' },
        { name: 'allergyName', label: i18n._({ id: 'changeRequest.field.name', message: 'Name' }), type: 'text' },
        {
          name: 'allergySeverity',
          label: i18n._({ id: 'changeRequest.field.severity', message: 'Severity' }),
          type: 'select',
          selectOptions: [
            { value: 'MILD', label: i18n._({ id: 'changeRequest.severity.MILD', message: 'MILD' }) },
            { value: 'MODERATE', label: i18n._({ id: 'changeRequest.severity.MODERATE', message: 'MODERATE' }) },
            { value: 'SEVERE', label: i18n._({ id: 'changeRequest.severity.SEVERE', message: 'SEVERE' }) },
          ],
        },
        { name: 'allergyComment', label: i18n._({ id: 'changeRequest.field.comment', message: 'Comment' }), type: 'textarea', colSpan: 2 },
      );
    } else if (subType === 'medical') {
      base.push(
        {
          name: 'medicalKind',
          label: i18n._({ id: 'changeRequest.field.kind', message: 'Kind' }),
          type: 'select',
          selectOptions: [
            { value: 'disease', label: i18n._({ id: 'changeRequest.kind.disease', message: 'Disease' }) },
            { value: 'medication', label: i18n._({ id: 'changeRequest.kind.medication', message: 'Medication' }) },
          ],
        },
        { name: 'medicalOperation', label: i18n._({ id: 'changeRequest.field.operation', message: 'Operation' }), type: 'select', selectOptions: operationOptions },
        { name: 'medicalId', label: i18n._({ id: 'changeRequest.field.medicalId', message: 'Record ID' }), type: 'text' },
        { name: 'medicalName', label: i18n._({ id: 'changeRequest.field.name', message: 'Name' }), type: 'text' },
        { name: 'medicalType', label: i18n._({ id: 'changeRequest.field.type', message: 'Type (disease)' }), type: 'text' },
        { name: 'medicalDosage', label: i18n._({ id: 'changeRequest.field.dosage', message: 'Dosage (medication)' }), type: 'text' },
        { name: 'medicalComment', label: i18n._({ id: 'changeRequest.field.comment', message: 'Comment' }), type: 'textarea', colSpan: 2 },
      );
    } else if (subType === 'contact_pickup' || subType === 'contact_emergency') {
      base.push(
        {
          name: 'contactId',
          label: i18n._({ id: 'changeRequest.field.contact', message: 'Contact' }),
          type: 'asyncselect',
          required: true,
          loadOptions: loadContactOptions,
          initialOptions: prefill?.contactId
            ? [{ value: prefill.contactId, label: prefill.contactId }]
            : [],
        },
        {
          name: 'contactGrant',
          label: i18n._({ id: 'changeRequest.field.grant', message: 'Grant (off = revoke)' }),
          type: 'booleantoggle',
        },
      );
    }

    return base;
  }, [subType, lockTarget, childInitialOptions, loadChildOptions, loadContactOptions, prefill?.contactId, i18n]);

  const groups: GroupConfig<FormValues>[] = useMemo(
    () => [
      {
        title: i18n._({ id: 'changeRequest.group.target', message: 'Target & change type' }),
        fields: ['childIds', 'subType'] as Array<keyof FormValues>,
      },
      {
        title: i18n._({ id: 'changeRequest.group.change', message: 'Change details' }),
        fields: fields.filter((f) => f.name !== 'childIds' && f.name !== 'subType').map((f) => f.name),
      },
    ],
    [fields, i18n],
  );

  const defaultValues: Partial<FormValues> = {
    childIds: targetChildIds,
    subType: initialSubType,
    commentField: prefill?.commentField ?? 'notes',
    commentValue: prefill?.commentValue ?? '',
    allergyOperation: prefill?.allergyOperation ?? 'add',
    allergyId: prefill?.allergyId ?? '',
    allergyName: prefill?.allergyName ?? '',
    allergySeverity: prefill?.allergySeverity ?? '',
    allergyComment: prefill?.allergyComment ?? '',
    medicalKind: prefill?.medicalKind ?? 'disease',
    medicalOperation: prefill?.medicalOperation ?? 'add',
    medicalId: prefill?.medicalId ?? '',
    medicalName: prefill?.medicalName ?? '',
    medicalType: prefill?.medicalType ?? '',
    medicalDosage: prefill?.medicalDosage ?? '',
    medicalComment: prefill?.medicalComment ?? '',
    contactId: prefill?.contactId ?? '',
    contactGrant: prefill?.contactGrant ?? true,
  };

  const handleSubmit = async (values: FormValues) => {
    // In-place review submit: PATCH the existing item + approve (parent-driven),
    // instead of creating a new request. `wasEdited` drives the server-recorded
    // outcome (edited vs approved_as_is).
    if (onReviewSubmit) {
      const wasEdited = !isSameFormValues(values, defaultValues);
      setReviewSubmitting(true);
      try {
        await onReviewSubmit(values, wasEdited);
        onSuccess?.();
      } finally {
        setReviewSubmitting(false);
      }
      return;
    }

    const batch = buildCreateChangeRequestBatch({
      subType: values.subType,
      childIds: values.childIds,
      comment:
        values.subType === 'comment'
          ? { field: values.commentField || 'notes', value: values.commentValue }
          : undefined,
      allergy:
        values.subType === 'allergy'
          ? {
              operation: values.allergyOperation,
              allergy_id: values.allergyId || undefined,
              name: values.allergyName || undefined,
              severity: values.allergySeverity || undefined,
              comment: values.allergyComment || undefined,
            }
          : undefined,
      medical:
        values.subType === 'medical'
          ? {
              kind: values.medicalKind,
              operation: values.medicalOperation,
              id: values.medicalId || undefined,
              name: values.medicalName || undefined,
              type: values.medicalType || undefined,
              dosage: values.medicalDosage || undefined,
              comment: values.medicalComment || undefined,
            }
          : undefined,
      contact:
        values.subType === 'contact_pickup' || values.subType === 'contact_emergency'
          ? { contact_id: values.contactId, grant: values.contactGrant }
          : undefined,
      source: source ?? { kind: 'web_admin' },
    });

    const res = await create.mutateAsync({ batch, childIds: values.childIds });
    onResult?.(res.results?.[0]);
    onSuccess?.();
  };

  // Keyboard: focus the first interactive control on mount (the target picker, or
  // the sub_type select when the target is locked — the locked picker is skipped
  // because it is disabled).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const first = root.querySelector<HTMLElement>(
      'button[role="combobox"]:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled])',
    );
    first?.focus();
  }, []);

  // Keyboard: Cmd/Ctrl+Enter submits the form from anywhere within it.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      const form = containerRef.current?.querySelector('form');
      if (form?.requestSubmit) {
        form.requestSubmit();
      } else {
        containerRef.current
          ?.querySelector<HTMLButtonElement>('button[type="submit"]')
          ?.click();
      }
    }
  };

  return (
    <div className="space-y-4" ref={containerRef} onKeyDown={handleKeyDown}>
      <div className="flex items-center gap-2">
        {isSensitiveSubType(subType) ? (
          <Badge variant="destructive" aria-label={i18n._({ id: 'changeRequest.badge.requiresApproval', message: 'Requires approval' })}>
            {i18n._({ id: 'changeRequest.badge.requiresApproval', message: 'Requires approval' })}
          </Badge>
        ) : (
          <Badge variant="secondary">
            {i18n._({ id: 'changeRequest.badge.mayAutoApply', message: 'May auto-apply' })}
          </Badge>
        )}
      </div>
      <BaseEditableForm<FormValues>
        key={prefillKey}
        schema={schema}
        fields={fields}
        groups={groups}
        defaultValues={defaultValues}
        mode="create"
        submitLabel={
          onReviewSubmit
            ? i18n._({ id: 'changeRequest.button.saveAndApprove', message: 'Save & approve' })
            : mode === 'review'
              ? i18n._({ id: 'changeRequest.button.saveResubmit', message: 'Save & re-submit' })
              : undefined
        }
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isSubmitting={create.isPending || reviewSubmitting}
        onFieldChange={(name, value) => {
          if (name === 'subType') {
            setSubType(String(value));
          }
        }}
      />
    </div>
  );
}
